import { useCallback, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { useTranslation } from "react-i18next";
import { cloneDeep, isEmpty, size } from "lodash";

import dataHistoryService from "@plannel/services/data/data-history-service";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridExportSetting from "@plannel/components/aggrid/GridExportSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

// 목록 응답의 errHistory 는 서버가 1,000건 남짓에서 자른다. 그 이상이면 내보낼 때 전체를 다시 받는다.
const FULL_FETCH_THRESHOLD = 1000;
// 원인 칩은 많은 순으로 이만큼만 — 오류명이 행마다 다른 파일이면 칩이 수백 개가 된다.
const MAX_CAUSE_CHIPS = 6;

const ImportErrorDetail = ({ data, title, onEmpty }) => {
  const { t } = useTranslation();
  const gridRef = useRef();
  const [cause, setCause] = useState(null);

  const gridSetting = useMemo(() => DefaultGridSetting({ title }), [title]);
  const errors = useMemo(() => data?.errHistory ?? [], [data]);

  const causes = useMemo(() => {
    const counts = errors.reduce((acc, { errorName }) => acc.set(errorName, (acc.get(errorName) ?? 0) + 1), new Map());
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [errors]);

  const rows = useMemo(() => (cause === null ? errors : errors.filter(e => e.errorName === cause)), [errors, cause]);
  const truncated = (data?.errorCnt ?? 0) > errors.length;

  const makeFileName = () => {
    const name = t(title).replace(/\s/gi, "_").toLowerCase();
    return `plannel_${t("error")}_${name}_${dateUtils.formatDate(new Date(), "yyyy-MM-dd_HH-mm-ss")}`;
  };

  const exportErrors = isExcel => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    const run = () => (isExcel
      ? api.exportDataAsExcel({ ...GridExportSetting.defaultExcelExportParams(title), fileName: `${makeFileName()}.xlsx` })
      : api.exportDataAsCsv({ ...GridExportSetting.defaultCsvExportParams(title), fileName: `${makeFileName()}.csv` }));

    if (size(errors) < FULL_FETCH_THRESHOLD) {
      run();
      return;
    }

    const params = cloneDeep(data);
    delete params.errHistory;

    dataHistoryService.getErrorHistories(params)
      .then(({ data: all }) => {
        if (isEmpty(all)) {
          onEmpty?.();
          return;
        }
        GridUtils.setRowData(api, cause === null ? all : all.filter(e => e.errorName === cause));
        run();
      })
      .catch(e => debug(e));
  };

  const getContextMenuItems = useCallback(params => [
    ...params.defaultItems.filter(item => item !== "paste" && item !== "export"),
    {
      name: "Export",
      icon: '<span class="ag-icon ag-icon-save"></span>',
      subMenu: [
        { name: "CSV Export", action: () => exportErrors(false), icon: '<span class="ag-icon ag-icon-csv"></span>' },
        { name: "Excel Export", action: () => exportErrors(true), icon: '<span class="ag-icon ag-icon-excel"></span>' },
      ],
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [errors, cause]);

  const causeChip = (key, label, count) => {
    const selected = cause === key;
    // 원인 문구가 길어 칩에서 말줄임되므로 전체 문구를 툴팁으로 보여준다("전체" 칩은 제외).
    return (
      <Tooltip key={key ?? "__all"} title={key === null ? "" : key} placement="top" arrow>
        <Chip
          size="small"
          clickable
          color={selected ? "primary" : "default"}
          variant={selected ? "filled" : "outlined"}
          onClick={() => setCause(key)}
          label={<>{label} <b>{count.toLocaleString()}</b></>}
          sx={{ maxWidth: 320, fontSize: FS.CAPTION }}
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1, p: 1.5, pl: 5, bgcolor: "grey.50" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        {causeChip(null, t("all"), errors.length)}
        {causes.slice(0, MAX_CAUSE_CHIPS).map(([name, count]) => causeChip(name, name, count))}
        {causes.length > MAX_CAUSE_CHIPS && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>+{causes.length - MAX_CAUSE_CHIPS}</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {truncated && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", mr: 1 }}>
            {t("MSG_ErrorPreviewLimited", { count: errors.length.toLocaleString() })}
          </Typography>
        )}
        <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={() => exportErrors(false)}>CSV</Button>
        <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={() => exportErrors(true)}>Excel</Button>
      </Box>
      <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
        {/*
          안쪽 그리드도 반드시 DefaultGridSetting(theme: "legacy")을 탄다. 빠지면 AG Grid 가 새 테마(Quartz) CSS 를
          주입해 이 그리드 모양이 달라질 뿐 아니라 바깥 그리드의 Balham 아이콘 폰트까지 깨진다(펼침 화살표가 검은 네모).
        */}
        <AgGridReact
          ref={gridRef}
          rowData={rows}
          {...gridSetting}
          columnDefs={[
            { headerName: "Error", field: "errorName", type: "nonEditableColumn", width: 320, tooltipField: "errorName" },
            { headerName: "Error Data", field: "errorData", type: "nonEditableColumn", flex: 1, tooltipField: "errorData" },
          ]}
          getContextMenuItems={getContextMenuItems}
        />
      </Box>
    </Box>
  );
};

export default ImportErrorDetail;
